import {
  OrganizationProvider,
  PredeploymentTestingPage,
} from '@hologram-hyper-dashboard/components';
import { getOrgIdFromContextData, getUserContextDataMemoized } from 'common-js/api/util';
import { HoloHelmet } from 'common-js/components';
import {
  ACCOUNT_LOGIN,
  pathMatchesWithIdsReplaced,
  USAGE,
  USAGE_EXITED_TESTING,
  USAGE_IN_TESTING,
  USAGE_TESTING_INVENTORY,
} from 'common-js/constants/paths';
import useAppSelector from 'common-js/hooks/useAppSelector';
import { selectHasPredeploymentTestingFlags } from 'common-js/reducers/releaseFlag/selectors';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { browserHistory } from 'react-router';

type UsagePredeploymentView = GetReactParameterBag<typeof PredeploymentTestingPage>['viewKey'];

interface UsagePredeploymentProps {
  location: { pathname: string };
}

const UsagePredeployment: FC<UsagePredeploymentProps> = ({ location }) => {
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = getOrgIdFromContextData(userContext);
  const isPreflight = useAppSelector(selectHasPredeploymentTestingFlags);
  const path = location.pathname;

  const view: { key: UsagePredeploymentView; title: string } | null = useMemo(() => {
    // Note: this has to be ordered here based on the the way path matching is implemented
    if (pathMatchesWithIdsReplaced(USAGE_IN_TESTING, path)) {
      return { key: 'intesting', title: 'SIMs in testing' };
    }
    if (pathMatchesWithIdsReplaced(USAGE_EXITED_TESTING, path)) {
      return { key: 'exitedtesting', title: 'Activation history' };
    }
    if (pathMatchesWithIdsReplaced(USAGE_TESTING_INVENTORY, path)) {
      return { key: 'testinginventory', title: 'Pre-deployment testing age analysis' };
    }

    return { key: 'intesting', title: 'SIMs in testing' };
  }, [path]);

  const handleTabClick = useCallback((url: string) => {
    browserHistory.push({ pathname: url });
  }, []);

  useEffect(() => {
    if (!orgId || !isPreflight) {
      browserHistory.push(ACCOUNT_LOGIN);
    }
  }, [isPreflight, orgId]);

  if (!orgId) {
    return null;
  }

  return (
    <>
      <HoloHelmet title="Usage Reports - Pre-deployment testing" />
      <OrganizationProvider organizationId={orgId}>
        <PredeploymentTestingPage
          className="full-height-no-padding"
          handleTabClick={handleTabClick}
          viewKey={view?.key}
          usageBaseUrl={USAGE}
        />
      </OrganizationProvider>
    </>
  );
};

export default UsagePredeployment;
